import { SessionPresentation } from '@/components/simulation/SessionPresentation';
import { useState, useEffect, useRef, useCallback } from 'react';
import { useAnamAvatar } from '@/hooks/useAnamAvatar';
import { PatientSessionInformation } from './PatientSessionInformation';
import { CameraCapture } from './CameraCapture';
import { SessionTimer } from './SessionTimer';
import { ResultGenerationDialog } from './ResultGenerationDialog';
import { supabase } from '@/integrations/supabase/client';
import { Button } from '@/components/ui/button';
import { Switch } from '@/components/ui/switch';
import { Loader2, Video, Phone, PhoneOff, User, AlertCircle } from 'lucide-react';
import { toast } from 'sonner';
import { reportUserFacingError } from '@/lib/errorReporter';
import { buildRuntimePrompt, buildRuntimeFirstMessage } from '@/lib/prompt/runtime';
import { processEvaluationGamification } from '@/lib/gamification/evaluationProcessor';
import { getConversationCriteria, consolidateTranscript } from '@/lib/evaluationCriteria';
import { stopMediaRecorder } from '@/lib/mediaRecorder';
import fixWebmDuration from 'fix-webm-duration';
import { getCameraAccessFailure, requestUserCameraStream } from '@/lib/cameraAccess';

interface AnamVideoSimulationProps {
  agentId: string;
  sessionId: string;
  userId: string;
  userName: string;
  userEmail: string;
  companyId: string;
  onSessionEnd: (transcript: any[], evaluationId: string) => void;
}

interface CapturedFrame {
  frame_number: number;
  timestamp: number;
  base64_image: string;
}

const MAX_FRAMES_TO_ANALYZE = 30; // Limit to prevent payload size issues

// Helper function to sample frames evenly
function sampleFramesEvenly(frames: CapturedFrame[], maxFrames: number): CapturedFrame[] {
  if (frames.length <= maxFrames) return frames;
  
  const step = frames.length / maxFrames;
  const sampled: CapturedFrame[] = [];
  
  for (let i = 0; i < maxFrames; i++) {
    const index = Math.floor(i * step);
    sampled.push(frames[index]);
  }
  
  return sampled;
}

// Helper function to poll for evaluation score (used when evaluation created without score initially)
async function pollForEvaluationScore(evaluationId: string, maxAttempts = 45): Promise<number> {

  for (let attempt = 0; attempt < maxAttempts; attempt++) {
    const { data, error } = await supabase
      .from('simulation_evaluations')
      .select('overall_score')
      .eq('id', evaluationId)
      .single();

    if (error) {
      console.error('Error polling for score:', error);
      await new Promise(resolve => setTimeout(resolve, 2000));
      continue;
    }

    // Check if score has been populated (non-zero and valid)
    if (data?.overall_score && data.overall_score > 0) {
      return data.overall_score;
    }

    await new Promise(resolve => setTimeout(resolve, 2000));
  }

  console.warn('⚠️ Score polling timed out after 90 seconds');
  return 0;
}

// Evaluate simulation via edge function (with retries for transient failures)
async function evaluateSimulationViaEdge(
  payload: Record<string, any>,
  opts: { maxAttempts?: number; baseDelayMs?: number } = {}
): Promise<{ success: boolean; evaluationId?: string; score?: number | null; hasAvatarRoleIssue?: boolean; error?: string }> {
  const maxAttempts = opts.maxAttempts ?? 3;
  const baseDelayMs = opts.baseDelayMs ?? 1000;

  let lastError: string | undefined;

  for (let attempt = 1; attempt <= maxAttempts; attempt++) {
    try {
      const { data, error } = await supabase.functions.invoke('evaluate-simulation', {
        body: payload
      });

      if (error) {
        lastError = error.message;
        console.error(`❌ Evaluation edge function failed (attempt ${attempt}/${maxAttempts}):`, error);
      } else if (data?.success) {
        return { success: true, evaluationId: data.evaluation_id, score: data.overall_score, hasAvatarRoleIssue: data.assessment_status === 'unscored_avatar_issue' };
      } else {
        lastError = data?.error || 'Evaluation failed';
        console.error(`❌ Evaluation returned unsuccessful (attempt ${attempt}/${maxAttempts}):`, data?.error);
      }
    } catch (err) {
      lastError = err instanceof Error ? err.message : 'Unknown error';
      console.error(`❌ Evaluation error (attempt ${attempt}/${maxAttempts}):`, err);
    }

    // Backoff before retrying (only if we still have attempts left)
    if (attempt < maxAttempts) {
      const delayMs = baseDelayMs * Math.pow(2, attempt - 1);
      await new Promise(resolve => setTimeout(resolve, delayMs));
    }
  }

  return { success: false, error: lastError || 'Evaluation failed' };
}

export function AnamVideoSimulation({
  agentId,
  sessionId,
  userId,
  userName,
  userEmail,
  companyId,
  onSessionEnd
}: AnamVideoSimulationProps) {
  const [cameraOn, setCameraOn] = useState(false);
  const cameraOnRef = useRef(false);
  const webcamGenerationRef = useRef(0);
  useEffect(() => () => {
    cameraOnRef.current = false;
    webcamGenerationRef.current += 1;
    webcamStreamRef.current?.getTracks().forEach(track => track.stop());
  }, []);
  const [agent, setAgent] = useState<any>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isRecording, setIsRecording] = useState(false);
  const [webcamFailureMessage, setWebcamFailureMessage] = useState<string | null>(null);
  const [capturedFrames, setCapturedFrames] = useState<CapturedFrame[]>([]);
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const [analysisStatus, setAnalysisStatus] = useState('');
  const [sessionStartTime, setSessionStartTime] = useState<string | null>(null);
  const [recordSession, setRecordSession] = useState(true);
  
  const videoContainerRef = useRef<HTMLVideoElement>(null);
  const userVideoRef = useRef<HTMLVideoElement>(null);
  const audioChunksRef = useRef<Blob[]>([]);
  const videoChunksRef = useRef<Blob[]>([]);
  const uploadedChunksRef = useRef<number>(0);
  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const videoRecorderRef = useRef<MediaRecorder | null>(null);
  const audioStreamRef = useRef<MediaStream | null>(null);
  const audioContextRef = useRef<AudioContext | null>(null);
  const audioDestinationRef = useRef<MediaStreamAudioDestinationNode | null>(null);
  const audioRecordingStartRef = useRef<number | null>(null);
  const agentAudioAttachedRef = useRef<boolean>(false);
  const videoAudioContextRef = useRef<AudioContext | null>(null);
  const videoRecordingStartRef = useRef<number | null>(null);
  const videoRecordingStartTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const videoRecordingPromiseRef = useRef<Promise<boolean> | null>(null);
  const sessionActiveRef = useRef(false);
  const webcamStreamRef = useRef<MediaStream | null>(null);
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const uploadIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const isProcessingEndRef = useRef(false);
  const sessionIdRef = useRef<string>(sessionId);
  const [webcamEnabled, setWebcamEnabled] = useState(false);

  const handleCameraVideoElementReady = useCallback((video: HTMLVideoElement | null) => {
    userVideoRef.current = video;
  }, []);

  // Initialize Anam with agent configuration (including first message for proper opening)
  const anam = useAnamAvatar({
    personaId: agent?.anam_persona_id || '',
    agentId: agent?.id,
    tenantId: agent?.tenant_id,
    // V2 only for explicitly converted agents; every other agent keeps its
    // existing builder and behaviour untouched.
    systemPrompt: agent ? buildRuntimePrompt(agent) : '',
    // Version-gated: V1 agents keep their exact opening; V2 agents introduce
    // themselves with persona_name, never the tile title.
    firstMessage: agent ? buildRuntimeFirstMessage(agent) : '',
    persistKey: sessionId, // back transcript up to localStorage so reload/disconnect doesn't lose it
    onTranscriptUpdate: () => {},
    onSessionEnd: () => {},
    onError: (error) => {
      toast.error(`Connection error: ${error}`);
    },
  });

  // Fetch agent data on mount
  useEffect(() => {
    async function fetchAgent() {
      try {
        const { data, error } = await supabase
          .from('simulation_agents')
          .select('*')
          .eq('id', agentId)
          .maybeSingle();

        if (error) throw error;
        if (!data) throw new Error('Agent not found');

        // Load configurable tone/disclosure prompts from system_settings (if available)
        // Wrapped in try-catch because regular users may not have RLS access to system_settings
        try {
          if (data.conversation_tone) {
            const { data: toneSetting } = await (supabase as any)
              .from('system_settings')
              .select('setting_value')
              .eq('setting_key', `tone_${data.conversation_tone}`)
              .maybeSingle();
            if (toneSetting?.setting_value?.prompt) {
              (data as any)._tonePromptOverride = toneSetting.setting_value.prompt;
            }
          }
          if (data.disclosure_style) {
            const { data: disclosureSetting } = await (supabase as any)
              .from('system_settings')
              .select('setting_value')
              .eq('setting_key', `disclosure_${data.disclosure_style}`)
              .maybeSingle();
            if (disclosureSetting?.setting_value?.prompt) {
              (data as any)._disclosurePromptOverride = disclosureSetting.setting_value.prompt;
            }
          }
        } catch {
          // Non-critical: fall back to built-in defaults if system_settings is not accessible
        }

        setAgent(data);
      } catch (err) {
        console.error('Failed to fetch agent:', err);
        toast.error('Failed to load agent configuration');
      } finally {
        setIsLoading(false);
      }
    }

    fetchAgent();
  }, [agentId]);

  // Initialize Anam once agent is loaded
  useEffect(() => {
    if (agent && agent.anam_persona_id) {
      anam.initialize();
    }
  }, [agent]);

  // Start webcam for user video display
  const startWebcam = async () => {
    const generation = ++webcamGenerationRef.current;
    try {
      const stream = await requestUserCameraStream({
        width: { ideal: 640 },
        height: { ideal: 480 },
        facingMode: { ideal: 'user' },
      });
      if (!cameraOnRef.current || generation !== webcamGenerationRef.current) {
        stream.getTracks().forEach(track => track.stop());
        return;
      }
      webcamStreamRef.current = stream;
      
      if (userVideoRef.current) {
        userVideoRef.current.srcObject = stream;
        userVideoRef.current.play();
      }
      setWebcamEnabled(true);
      setWebcamFailureMessage(null);
    } catch (err) {
      const failure = getCameraAccessFailure(err);
      console.warn(`Webcam unavailable (${failure.code})`, err);
      setWebcamEnabled(false);
      setWebcamFailureMessage(failure.message);
    }
  };

  // Stop webcam
  const stopWebcam = () => {
    webcamGenerationRef.current += 1;
    if (webcamStreamRef.current) {
      webcamStreamRef.current.getTracks().forEach(track => track.stop());
      webcamStreamRef.current = null;
    }
    if (userVideoRef.current && !agent?.enable_vision_evaluation) userVideoRef.current.srcObject = null;
    setWebcamEnabled(false);
  };

  // Start audio recording: mic + agent voice mixed via Web Audio API.
  // Mic starts immediately; agent audio is attached as soon as the Anam <video> exposes a captureStream.
  // Final blob is duration-patched so the player can scrub.
  const startAudioRecording = async () => {
    try {
      const micStream = await navigator.mediaDevices.getUserMedia({ audio: true });
      audioStreamRef.current = micStream;

      const audioContext = new AudioContext();
      const destination = audioContext.createMediaStreamDestination();
      const micSource = audioContext.createMediaStreamSource(micStream);
      micSource.connect(destination);

      audioContextRef.current = audioContext;
      audioDestinationRef.current = destination;
      agentAudioAttachedRef.current = false;

      const mimeType = MediaRecorder.isTypeSupported('audio/webm;codecs=opus')
        ? 'audio/webm;codecs=opus'
        : 'audio/webm';

      const recorder = new MediaRecorder(destination.stream, { mimeType });

      recorder.ondataavailable = (e) => {
        if (e.data.size > 0) {
          audioChunksRef.current.push(e.data);
        }
      };

      recorder.start(1000); // Capture every second
      mediaRecorderRef.current = recorder;
      audioRecordingStartRef.current = Date.now();

      attachAgentAudioToRecording();
    } catch (err) {
      console.error('Failed to start audio recording:', err);
    }
  };

  // Route the Anam agent's audio track into the recording mix as soon as it's available.
  // Event-driven (video `playing`/`loadeddata`) + 30s polling backstop for slow connections.
  const attachAgentAudioToRecording = () => {
    const audioContext = audioContextRef.current;
    const destination = audioDestinationRef.current;
    if (!audioContext || !destination) return;

    const tryAttach = (): boolean => {
      if (agentAudioAttachedRef.current) return true;
      const agentVideo = videoContainerRef.current;
      if (!agentVideo || !agentVideo.videoWidth) return false;
      try {
        const agentMediaStream = (agentVideo as any).captureStream?.() || (agentVideo as any).mozCaptureStream?.();
        const audioTracks = agentMediaStream?.getAudioTracks?.() || [];
        if (audioTracks.length === 0) return false;
        const agentSource = audioContext.createMediaStreamSource(new MediaStream(audioTracks));
        agentSource.connect(destination);
        agentAudioAttachedRef.current = true;
        console.log('🎤 Agent audio attached to audio recording');
        return true;
      } catch (err) {
        console.warn('🎤 Failed to attach agent audio (will retry):', err);
        return false;
      }
    };

    const wireVideoListener = () => {
      const agentVideo = videoContainerRef.current;
      if (!agentVideo) return;
      const onPlaying = () => {
        if (tryAttach()) {
          agentVideo.removeEventListener('playing', onPlaying);
          agentVideo.removeEventListener('loadeddata', onPlaying);
        }
      };
      agentVideo.addEventListener('playing', onPlaying);
      agentVideo.addEventListener('loadeddata', onPlaying);
    };
    wireVideoListener();

    (async () => {
      for (let attempts = 0; attempts < 100; attempts++) {
        if (agentAudioAttachedRef.current) return;
        if (tryAttach()) return;
        if (attempts === 5) wireVideoListener();
        await new Promise(r => setTimeout(r, 300));
      }
      console.warn('🎤 Could not attach agent audio after 30s — audio file will contain mic only');
    })();
  };

  const stopAudioRecording = async (): Promise<Blob | null> => {
    const recorder = mediaRecorderRef.current;
    const startedAt = audioRecordingStartRef.current;

    await stopMediaRecorder(recorder);

    let audioBlob: Blob | null = null;
    if (audioChunksRef.current.length > 0) {
      const mimeType = audioChunksRef.current[0]?.type || 'audio/webm';
      const rawBlob = new Blob(audioChunksRef.current, { type: mimeType });
      const durationMs = startedAt ? Date.now() - startedAt : 0;
      audioBlob = rawBlob;

      if (mimeType.includes('webm') && durationMs > 0) {
        try {
          audioBlob = await fixWebmDuration(rawBlob, durationMs);
        } catch (err) {
          console.warn('Failed to patch audio webm duration — using raw blob:', err);
        }
      }
    }

    if (audioStreamRef.current) {
      audioStreamRef.current.getTracks().forEach(track => track.stop());
      audioStreamRef.current = null;
    }
    if (audioContextRef.current && audioContextRef.current.state !== 'closed') {
      await audioContextRef.current.close().catch(() => {});
    }
    audioContextRef.current = null;
    audioDestinationRef.current = null;
    mediaRecorderRef.current = null;
    audioRecordingStartRef.current = null;
    agentAudioAttachedRef.current = false;

    return audioBlob;
  };

  // Start video recording once Anam begins producing frames. Slow corporate
  // networks can take longer than the previous fixed five-second window.
  const startVideoRecording = async (): Promise<boolean> => {
    try {
      let agentVideo = videoContainerRef.current;
      for (let attempt = 0; attempt < 100; attempt++) {
        if (!sessionActiveRef.current) return false;
        agentVideo = videoContainerRef.current;
        if (agentVideo?.videoWidth && agentVideo.videoHeight) break;
        await new Promise(resolve => setTimeout(resolve, 300));
      }

      if (!agentVideo?.videoWidth || !agentVideo.videoHeight || !sessionActiveRef.current) {
        console.warn('⚠️ Video recording did not start because the avatar stream was not ready');
        return false;
      }

      const userVideo = userVideoRef.current;

      // Log user video status for debugging
      if (!userVideo?.videoWidth || userVideo.videoWidth === 0) {
        console.warn('⚠️ User webcam not ready - recording will show placeholder');
      }

      // Check if canvas.captureStream is supported
      const testCanvas = document.createElement('canvas');
      if (typeof testCanvas.captureStream !== 'function') {
        console.warn('⚠️ Video recording is not supported by this browser');
        return false;
      }

      // Create split-screen canvas (agent on right, user on left)
      const agentW = agentVideo.videoWidth;
      const agentH = agentVideo.videoHeight;
      const userW = userVideo?.videoWidth || 320;
      const userH = userVideo?.videoHeight || 240;
      
      // Scale user video to match agent video height
      const userScale = agentH / userH;
      const scaledUserW = Math.round(userW * userScale);
      
      const canvas = document.createElement('canvas');
      canvas.width = scaledUserW + agentW; // Side by side
      canvas.height = agentH;
      canvasRef.current = canvas;
      const ctx = canvas.getContext('2d');
      
      if (!ctx) {
        console.warn('⚠️ Video recording canvas could not be initialised');
        return false;
      }
      
      // Capture video stream from canvas
      const canvasStream = canvas.captureStream(30); // 30 fps
      
      // Combine audio: user mic + agent audio (from video element)
      const audioContext = new AudioContext();
      videoAudioContextRef.current = audioContext;
      const destination = audioContext.createMediaStreamDestination();
      
      // Add user microphone audio
      const micStream = audioStreamRef.current;
      if (micStream) {
        const micSource = audioContext.createMediaStreamSource(micStream);
        micSource.connect(destination);
      }
      
      // Add agent audio from video element using captureStream
      // (createMediaElementSource fails if element is already connected or CORS-restricted)
      try {
        // Try captureStream first - works with most video elements
        const agentMediaStream = (agentVideo as any).captureStream?.() || (agentVideo as any).mozCaptureStream?.();
        if (agentMediaStream) {
          const audioTracks = agentMediaStream.getAudioTracks();
          if (audioTracks.length > 0) {
            const agentAudioSource = audioContext.createMediaStreamSource(new MediaStream(audioTracks));
            agentAudioSource.connect(destination);
          } else {
            console.warn('⚠️ No audio tracks in agent video captureStream');
          }
        } else {
          // Fallback to createMediaElementSource (may fail)
          const agentAudioSource = audioContext.createMediaElementSource(agentVideo);
          agentAudioSource.connect(destination);
          agentAudioSource.connect(audioContext.destination);
        }
      } catch (err) {
        console.warn('⚠️ Could not capture agent audio:', err);
        // Try one more fallback - capture from audio context destination
      }
      
      // Add combined audio to canvas stream
      destination.stream.getAudioTracks().forEach(track => {
        canvasStream.addTrack(track);
      });

      // Use efficient codec with lower bitrate for manageable file sizes
      // 1Mbps = ~7.5MB per minute, ~75MB for 10 min session
      const mimeType = MediaRecorder.isTypeSupported('video/webm;codecs=vp9,opus')
        ? 'video/webm;codecs=vp9,opus'
        : MediaRecorder.isTypeSupported('video/webm;codecs=vp8,opus')
        ? 'video/webm;codecs=vp8,opus'
        : 'video/webm';

      const recorder = new MediaRecorder(canvasStream, { 
        mimeType, 
        videoBitsPerSecond: 1000000,  // 1Mbps - good quality, smaller files
        audioBitsPerSecond: 64000     // 64kbps audio
      });
      
      recorder.ondataavailable = (e) => {
        if (e.data.size > 0) {
          videoChunksRef.current.push(e.data);
        }
      };
      
      // Draw both video frames to canvas (split screen: user left, agent right)
      const drawFrame = () => {
        if (ctx) {
          // Clear canvas
          ctx.fillStyle = '#1a1a1a';
          ctx.fillRect(0, 0, canvas.width, canvas.height);
          
          // Draw user video on the left (if available)
          // Re-read the ref on every frame. Camera permission can resolve just
          // after recording starts; a one-time snapshot would preserve the
          // placeholder for the whole replay even after the preview is live.
          const currentUserVideo = userVideoRef.current;
          if (cameraOnRef.current && currentUserVideo && currentUserVideo.videoWidth > 0 && !currentUserVideo.paused) {
            ctx.drawImage(currentUserVideo, 0, 0, scaledUserW, agentH);
          } else {
            // Placeholder for user video
            ctx.fillStyle = '#333';
            ctx.fillRect(0, 0, scaledUserW, agentH);
            ctx.fillStyle = '#666';
            ctx.font = '24px sans-serif';
            ctx.textAlign = 'center';
            ctx.fillText('Your Camera', scaledUserW / 2, agentH / 2);
          }
          
          // Draw agent video on the right
          if (agentVideo && !agentVideo.paused && !agentVideo.ended) {
            ctx.drawImage(agentVideo, scaledUserW, 0, agentW, agentH);
          }
        }
        if (videoRecorderRef.current?.state === 'recording') {
          requestAnimationFrame(drawFrame);
        }
      };
      
      recorder.start(1000); // Capture every second
      videoRecorderRef.current = recorder;
      videoRecordingStartRef.current = Date.now();
      drawFrame();
      
      // Start progressive upload (every 60 seconds to avoid too many uploads)
      uploadIntervalRef.current = setInterval(async () => {
        if (videoChunksRef.current.length > uploadedChunksRef.current) {
          const newChunks = videoChunksRef.current.slice(uploadedChunksRef.current);
          const chunkBlob = new Blob(newChunks, { type: 'video/webm' });
          const chunkIndex = uploadedChunksRef.current;
          uploadedChunksRef.current = videoChunksRef.current.length;
          
          // Upload chunk via edge function (bypasses RLS)
          try {
            const { error } = await supabase.functions.invoke(
              `upload-recording?sessionId=${sessionIdRef.current}&chunkIndex=${chunkIndex}`,
              {
                body: chunkBlob,
                headers: { 'Content-Type': 'video/webm' },
              }
            );
            if (error) console.error('⚠️ Chunk upload failed:', error);
          } catch (err) {
            console.error('⚠️ Chunk upload error:', err);
          }
        }
      }, 60000); // Upload progress every 60 seconds

      return true;
    } catch (err) {
      console.error('Failed to start video recording:', err);
      return false;
    }
  };

  const stopVideoRecording = async (): Promise<Blob | null> => {
    if (uploadIntervalRef.current) {
      clearInterval(uploadIntervalRef.current);
      uploadIntervalRef.current = null;
    }

    const recorder = videoRecorderRef.current;
    const startedAt = videoRecordingStartRef.current;
    await stopMediaRecorder(recorder);
    recorder?.stream.getTracks().forEach(track => track.stop());

    videoRecorderRef.current = null;
    videoRecordingStartRef.current = null;

    if (videoAudioContextRef.current && videoAudioContextRef.current.state !== 'closed') {
      await videoAudioContextRef.current.close().catch(() => {});
    }
    videoAudioContextRef.current = null;

    if (videoChunksRef.current.length === 0) return null;

    const rawBlob = new Blob(videoChunksRef.current, { type: 'video/webm' });
    const durationMs = startedAt ? Date.now() - startedAt : 0;
    if (durationMs <= 0) return rawBlob;

    try {
      return await fixWebmDuration(rawBlob, durationMs);
    } catch (err) {
      console.warn('Failed to patch video webm duration — using raw blob:', err);
      return rawBlob;
    }
  };

  const uploadSessionRecording = async (
    blob: Blob,
    mediaType: 'video' | 'audio',
  ): Promise<string | null> => {
    const { data, error } = await supabase.functions.invoke(
      `upload-recording?sessionId=${sessionId}&final=true&mediaType=${mediaType}`,
      {
        body: blob,
        headers: {
          'Content-Type': mediaType === 'audio' ? 'audio/webm' : 'video/webm',
        },
      },
    );

    if (error || !data?.url) {
      console.error(`⚠️ Failed to upload ${mediaType} recording:`, error || data?.error);
      return null;
    }

    return data.url;
  };

  // Start simulation
  const handleStartSimulation = async () => {
    try {
      // Vision-enabled agents already own their camera stream through
      // CameraCapture. Request a separate stream only for the plain preview
      // path so the browser never opens two competing camera streams.
      if (cameraOnRef.current && !agent.enable_vision_evaluation) {
        await startWebcam();
      }
      
      await anam.startStreaming('anam-avatar-video');
      setIsRecording(true);
      setCapturedFrames([]);
      audioChunksRef.current = [];
      videoChunksRef.current = [];
      uploadedChunksRef.current = 0;
      sessionActiveRef.current = true;
      setSessionStartTime(new Date().toISOString());

      if (recordSession) {
        // Start audio recording as fallback for transcript
        await startAudioRecording();

        // Begin shortly after streaming starts, then keep waiting for Anam
        // frames for up to 30 seconds on slower networks.
        videoRecordingStartTimerRef.current = setTimeout(() => {
          videoRecordingPromiseRef.current = startVideoRecording();
        }, 1000);
      }
      
      toast.success('Simulation started!');
    } catch (err) {
      sessionActiveRef.current = false;
      if (videoRecordingStartTimerRef.current) {
        clearTimeout(videoRecordingStartTimerRef.current);
        videoRecordingStartTimerRef.current = null;
      }
      await stopVideoRecording();
      await stopAudioRecording();
      stopWebcam();
      reportUserFacingError(err, {
        userMessage: "We couldn't start your simulation. This is usually a temporary connection issue with the video avatar service.",
        area: 'simulation',
        action: 'start_simulation',
        metadata: { agent_id: agentId, agent_name: agent?.name },
      });
    }
  };

  // End simulation
  const handleEndSimulation = async () => {
    // Guard against double-clicks
    if (isProcessingEndRef.current) {
      return;
    }
    isProcessingEndRef.current = true;
    setIsAnalyzing(true);
    setAnalysisStatus(recordSession ? 'Saving your recording...' : 'Preparing your evaluation...');

    sessionActiveRef.current = false;
    // Keep End Session immediately silent even if recorder finalisation needs
    // to wait for a pending avatar-video start.
    anam.interruptPersona();
    if (videoRecordingStartTimerRef.current) {
      clearTimeout(videoRecordingStartTimerRef.current);
      videoRecordingStartTimerRef.current = null;
    }
    if (videoRecordingPromiseRef.current) {
      await videoRecordingPromiseRef.current.catch(() => false);
      videoRecordingPromiseRef.current = null;
    }

    setIsRecording(false);

    // Finalise recorders while their source streams are still live. Waiting for
    // MediaRecorder's stop event guarantees the final data chunk is included.
    const videoBlob = recordSession ? await stopVideoRecording() : null;
    const audioBlob = recordSession ? await stopAudioRecording() : null;

    await anam.stopStreaming();
    stopWebcam();

    // Upload final video recording to Supabase Storage
    let videoUrl: string | null = null;
    if (recordSession && videoBlob) {
      try {
        const fileSizeMB = videoBlob.size / 1024 / 1024;

        // Warn if file is very large (>100MB)
        if (fileSizeMB > 100) {
          console.warn(`⚠️ Large recording: ${fileSizeMB.toFixed(0)}MB - upload may take a while`);
        }

        // For very large files (>200MB), skip upload and just use audio
        if (fileSizeMB > 200) {
          toast.info('Session was too long for video recording. Transcript saved.');
        } else {
          videoUrl = await uploadSessionRecording(videoBlob, 'video');
        }
      } catch (uploadErr) {
        console.error('⚠️ Error uploading video:', uploadErr);
      }
    }

    // A mixed audio replay is captured alongside video. If canvas/video
    // capture is unavailable or its upload fails, preserve that audio instead
    // of silently saving the evaluation without any replay.
    if (recordSession && !videoUrl && audioBlob && audioBlob.size > 1_000) {
      videoUrl = await uploadSessionRecording(audioBlob, 'audio');
    }

    if (recordSession && !videoUrl) {
      reportUserFacingError(new Error('Session recording could not be saved'), {
        userMessage: 'Your session and evaluation are safe, but the replay recording could not be stored.',
        area: 'simulation',
        action: 'save_recording',
        metadata: { agent_id: agentId, agent_name: agent?.name, session_id: sessionId },
      });
    }

    // Get auth user first
    const { data: { user: authUser } } = await supabase.auth.getUser();
    if (!authUser) {
      toast.error('Authentication required');
      setIsAnalyzing(false);
      setAnalysisStatus('');
      isProcessingEndRef.current = false;
      return;
    }

    setAnalysisStatus('Processing video...');

    try {
      // 1. Analyze video frames if captured
      let visionData = null;
      if (capturedFrames.length > 0 && agent.enable_vision_evaluation) {
        // Sample frames to max 30 to prevent payload size issues
        const framesToAnalyze = sampleFramesEvenly(capturedFrames, MAX_FRAMES_TO_ANALYZE);
        
        setAnalysisStatus(`Analyzing ${framesToAnalyze.length} video frames...`);
        
        const { data, error } = await supabase.functions.invoke('analyze-vision-frames', {
          body: {
            session_id: sessionId,
            agent_id: agentId,
            agent_name: agent.name,
            agent_type: agent.type,
            user_id: userId,
            auth_user_id: authUser.id,
            user_name: userName,
            user_email: userEmail,
            company_id: companyId,
            frames: framesToAnalyze,
            vision_focus_areas: agent.vision_focus_areas || ['eye_contact', 'posture', 'background'],
            vision_instructions: agent.vision_instructions || ''
          }
        });

        if (error) throw error;
        visionData = data;
      }

      // 2. Get transcript from Anam SDK
      let transcript = anam.getTranscript();
      
      // Check if transcript has actual content
      const hasContent = transcript.some(m => m.content && m.content.trim());
      
      // Consolidate transcript into readable format
      let finalTranscript = '';
      if (hasContent) {
        finalTranscript = consolidateTranscript(transcript, agent.name);
      }
      
      // Fallback: If SDK transcript is empty, try audio transcription
      if (!hasContent && recordSession && audioChunksRef.current.length > 0) {
        setAnalysisStatus('Transcribing audio...');
        
        try {
          // Reuse the fully finalised audio blob when available.
          const transcriptAudioBlob = audioBlob || new Blob(audioChunksRef.current, { type: 'audio/webm' });
          
          if (transcriptAudioBlob.size > 10000) { // Only proceed if audio is large enough
            // Convert to base64
            const reader = new FileReader();
            const base64Audio = await new Promise<string>((resolve, reject) => {
              reader.onloadend = () => {
                const result = reader.result as string;
                const base64 = result.split(',')[1];
                resolve(base64);
              };
              reader.onerror = reject;
              reader.readAsDataURL(transcriptAudioBlob);
            });
            
            // Transcribe via edge function
            const { data: transcriptionData, error: transcriptionError } = await supabase.functions.invoke(
              'transcribe-user-audio',
              { body: { audio: base64Audio, session_id: sessionId } }
            );
            
            if (transcriptionError) {
              console.error('Transcription error:', transcriptionError);
            } else if (transcriptionData?.text) {
              finalTranscript = `You: ${transcriptionData.text}`;
              transcript = [{
                role: 'user' as const,
                content: transcriptionData.text,
                timestamp: Date.now()
              }];
            }
          } else {
            console.warn('⚠️ Audio too small for transcription');
          }
        } catch (transcriptionError) {
          console.error('Failed to transcribe audio:', transcriptionError);
        }
      }
      
      if (!finalTranscript) {
        finalTranscript = 'Transcript unavailable';
      }
      

      // 3. Get evaluation ID from vision analysis or create new record
      let evaluationId = visionData?.evaluation_id;

      // 4. Evaluate via edge function (creates evaluation record and returns score)
      setAnalysisStatus('Evaluating your performance...');

      // Get conversation criteria based on agent type
      const conversationCriteria = getConversationCriteria(agent.type);

      const evaluationPayload = {
        session_id: sessionId,
        simulation_id: agentId,
        agent_id: agentId,
        agent_name: agent.name,
        agent_type: agent.type,
        user_id: userId,
        auth_user_id: authUser.id,
        user_name: userName,
        user_email: userEmail,
        company_id: companyId,
        platform: 'anam',
        conversation_transcript: finalTranscript,
        evaluation_criteria: JSON.stringify(conversationCriteria),
        evaluation_knowledge_sources: agent.evaluation_knowledge_sources || [],
        knowledge_sources: agent.knowledge_sources || [],
        goal_score: agent.goal_score || 80,
        session_start: sessionStartTime,
        session_end: new Date().toISOString(),
        include_conversation_references: true,
        video_recording_url: videoUrl
      };

      const evalResult = await evaluateSimulationViaEdge(evaluationPayload);

      let finalScore = 0;

      if (evalResult.success && evalResult.evaluationId) {
        evaluationId = evalResult.evaluationId;
        finalScore = evalResult.score || 0;
        if (evalResult.hasAvatarRoleIssue) toast.info('Session saved without a score because an avatar role issue was detected. You can start a new session.');
        else toast.success(`Evaluation complete! Score: ${finalScore}%`);
      } else {
        // Fallback: create basic evaluation record if edge function failed
        console.error('Edge function failed, creating fallback evaluation record');
        const { data: fallbackEval } = await supabase
          .from('simulation_evaluations')
          .insert({
            session_id: sessionId,
            agent_id: agentId,
            simulation_id: agentId,
            user_id: userId,
            auth_user_id: authUser.id,
            company_id: companyId,
            agent_name: agent.name,
            agent_type: agent.type,
            user_name: userName,
            user_email: userEmail,
            tenant_id: agent.tenant_id,
            transcript: finalTranscript,
            is_practice: true,
            vision_enabled: !!visionData,
            evaluated_at: new Date().toISOString()
          })
          .select()
          .single();

        if (fallbackEval) {
          evaluationId = fallbackEval.id;
        }
        toast.warning('Evaluation saved. Scoring is temporarily unavailable — we’ll keep trying in the background.');
      }

      // 5. Process gamification with actual score
      if (!evalResult.hasAvatarRoleIssue) await processEvaluationGamification(
        userId,
        evaluationId,
        agentId,
        agent.name,
        finalScore,
        agent.goal_score
      );
      
      onSessionEnd(transcript, evaluationId);

    } catch (err: any) {
      console.error('❌ Analysis error:', err);
      reportUserFacingError(err, {
        userMessage: "Your session was saved, but we hit a problem scoring it. Your recording and transcript are safe — the evaluation will be retried.",
        area: 'simulation',
        action: 'evaluate_session',
        metadata: { agent_id: agentId, agent_name: agent?.name },
      });

      // Get auth user for fallback insert
      const { data: { user: authUser } } = await supabase.auth.getUser();
      
      if (!authUser) {
        toast.error('Authentication error');
        return;
      }
      
      // Get transcript even in error case
      const transcript = anam.getTranscript();
      
      // Create basic evaluation record as fallback
      const { data: evalData } = await supabase
        .from('simulation_evaluations')
        .insert({
          session_id: sessionId,
          agent_id: agentId,
          simulation_id: agentId,
          user_id: userId,
          auth_user_id: authUser.id,
          company_id: companyId,
          agent_name: agent?.name,
          agent_type: agent?.type,
          user_name: userName,
          user_email: userEmail,
          tenant_id: agent?.tenant_id,
          vision_enabled: false,
          is_practice: true,
          is_submitted: false
        })
        .select()
        .single();

      if (evalData) {
        // Try to evaluate via edge function in error recovery path

        const conversationCriteria = getConversationCriteria(agent.type);
        const consolidatedTranscript = consolidateTranscript(transcript, agent.name);

        const evaluationPayload = {
          session_id: sessionId,
          simulation_id: agentId,
          agent_id: agentId,
          agent_name: agent.name,
          agent_type: agent.type,
          user_id: userId,
          auth_user_id: authUser.id,
          user_name: userName,
          user_email: userEmail,
          company_id: companyId,
          platform: 'anam',
          conversation_transcript: consolidatedTranscript,
          evaluation_criteria: JSON.stringify(conversationCriteria),
          evaluation_knowledge_sources: agent.evaluation_knowledge_sources || [],
          knowledge_sources: agent.knowledge_sources || [],
          goal_score: agent.goal_score || 80,
          session_start: sessionStartTime,
          session_end: new Date().toISOString(),
          include_conversation_references: true
        };

        setAnalysisStatus('Evaluating your performance...');
        const evalResult = await evaluateSimulationViaEdge(evaluationPayload);

        if (evalResult.success) {
          if (evalResult.hasAvatarRoleIssue) toast.info('Session saved without a score because an avatar role issue was detected. You can start a new session.');
          else toast.success(`Evaluation complete! Score: ${evalResult.score}%`);
          onSessionEnd(transcript, evalResult.evaluationId || evalData.id);
        } else {
          toast.warning('Evaluation saved, but scoring failed.');
          onSessionEnd(transcript, evalData.id);
        }
      }
    } finally {
      setIsAnalyzing(false);
      setAnalysisStatus('');
      setSessionStartTime(null); // Reset timer state for next session
      isProcessingEndRef.current = false;
    }
  };

  // Handle camera frame capture
  const handleFrameCaptured = (frame: CapturedFrame) => {
    setCapturedFrames(prev => [...prev, frame]);
  };

  const handleCameraError = (error: string) => {
    console.warn('Camera unavailable:', error);
    toast.warning(error);
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-96">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
        <span className="ml-3">Loading simulation...</span>
      </div>
    );
  }

  if (!agent) {
    return (
      <div className="text-center p-8">
        <p className="text-red-500">Failed to load agent configuration</p>
      </div>
    );
  }

  return (
    <SessionPresentation>
    <div className="session-page mx-auto w-full max-w-[1600px] px-3 py-3 space-y-3 md:px-5">
      {/* Video Avatar Error Banner */}
      {anam.error && (
        <div className="bg-destructive/10 border border-destructive/20 rounded-xl p-4">
          <div className="flex items-start gap-3">
            <div className="text-destructive">⚠️</div>
            <div className="flex-1">
              <h4 className="font-semibold text-destructive mb-1">Avatar Connection Failed</h4>
              <p className="text-sm text-destructive/80 mb-2">
                Failed to start the video avatar session. Please try again.
              </p>
              <p className="text-xs text-destructive/70">
                Please check the browser console logs and contact support with the error details.
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Connection state banners — fed by Anam SDK lifecycle events.
          Particularly important for users on high-latency / restricted networks
          (e.g. China via VPN) where WebSocket resets are frequent. */}
      {!anam.error && anam.connectionState === 'reconnecting' && isRecording && (
        <div className="bg-amber-50 border border-amber-300 rounded-xl p-3 flex items-center gap-3">
          <div className="w-3 h-3 rounded-full bg-amber-500 animate-pulse" />
          <div className="flex-1">
            <p className="text-sm font-medium text-amber-900">Reconnecting to avatar…</p>
            <p className="text-xs text-amber-800">
              Network blip detected. Your transcript so far is saved — keep this tab open.
            </p>
          </div>
        </div>
      )}
      {!anam.error && anam.connectionState === 'disconnected' && isRecording && (
        <div className="bg-red-50 border border-red-300 rounded-xl p-3 flex items-start gap-3">
          <div className="text-red-600 text-lg">⚠️</div>
          <div className="flex-1">
            <p className="text-sm font-medium text-red-900">Connection lost</p>
            <p className="text-xs text-red-800 mb-2">
              The avatar session couldn't recover. Your transcript ({anam.transcript.length} turns) is saved locally.
              {anam.lastDisconnectReason && ` Reason: ${anam.lastDisconnectReason}.`}
            </p>
            <p className="text-xs text-red-800">
              Please end this session and start a new one. If this keeps happening, you may be on a restricted network — try a different connection or VPN.
            </p>
          </div>
        </div>
      )}

      {/* Professional Session Header */}
      <div className="session-heading rounded-xl border bg-card px-4 py-3">
        <div className="flex items-center gap-3">
          {/* Agent Avatar with Pulse Ring */}
          <div className="relative flex-shrink-0">
            <div className="w-11 h-11 rounded-full bg-gradient-to-br from-primary/20 to-primary/5 flex items-center justify-center overflow-hidden ring-4 ring-primary/20">
              {agent.image_url ? (
                <img src={agent.image_url} alt={agent.name} className="w-full h-full object-cover" />
              ) : (
                <span className="text-3xl font-bold text-primary">{agent.name.substring(0, 2).toUpperCase()}</span>
              )}
            </div>
            {/* Live Recording Pulse Animation */}
            {isRecording && (
              <div className="absolute inset-0 rounded-full border-4 border-green-500 animate-ping opacity-30" />
            )}
          </div>
          
          {/* Session Info */}
          <div className="min-w-0 flex-1">
            <h2 className="text-base font-semibold leading-snug">{agent.name}</h2>
            <p className="text-muted-foreground text-xs">{agent.title || `${agent.type.charAt(0).toUpperCase() + agent.type.slice(1)} Simulation`}</p>
            <div className="mt-1 flex items-center gap-2 flex-wrap">
              <span className="px-2 py-0.5 bg-primary/10 text-primary rounded-full text-xs font-medium capitalize">
                {agent.type}
              </span>
              {isRecording && (
                <span className="flex items-center gap-2 px-2 py-0.5 bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400 rounded-full text-sm font-medium">
                  <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse" />
                  Live Session
                </span>
              )}
              {/* Session Timer */}
              <SessionTimer
                maxDurationSeconds={agent?.max_session_duration_seconds || 1200}
                startTime={isRecording && sessionStartTime ? new Date(sessionStartTime) : null}
                isActive={isRecording}
                onTimeExpired={handleEndSimulation}
              />
            </div>
          </div>
        </div>
      </div>

      <div className="session-camera-control flex items-center justify-between gap-3 rounded-xl border bg-card px-4 py-2">
        <div>
          <label htmlFor="session-camera" className="font-medium">Your camera</label>
          <p className="text-xs text-muted-foreground">Microphone stays on. Turning camera off stops new video capture.</p>
          <details className="session-camera-details text-xs text-muted-foreground"><summary className="cursor-pointer">Camera and recording details</summary><p>Previously captured video and frames remain in this session.</p></details>
        </div>
        <Switch id="session-camera" checked={cameraOn} onCheckedChange={(enabled) => {
          cameraOnRef.current = enabled;
          setCameraOn(enabled);
          if (!enabled) stopWebcam();
          else if (isRecording && !agent.enable_vision_evaluation) void startWebcam();
        }} />
      </div>
      {/* Keep the avatar mounted when changing camera or clinical tabs. */}
      <div data-camera={cameraOn ? 'on' : 'off'} className={`session-stage grid grid-cols-1 gap-3 ${cameraOn ? 'md:grid-cols-2' : agent.type === 'patient' ? 'lg:grid-cols-2' : ''}`}>
        {/* User and avatar panels share exactly the same dimensions */}
        <div className={`${cameraOn ? 'flex' : 'hidden'} session-video-tile min-w-0 flex-col bg-card border border-border rounded-xl overflow-hidden`}>
          <div className="shrink-0 min-h-11 bg-muted/40 px-3 py-2 border-b border-border flex items-center justify-between gap-2">
            <h3 className="min-w-0 truncate text-sm font-medium flex items-center gap-2">
              <Video className="w-4 h-4 text-primary" />
              Your Camera
            </h3>
            {isRecording && capturedFrames.length > 0 && (
              <span className="text-xs bg-primary/20 text-primary px-2 py-1 rounded-full font-medium">
                {capturedFrames.length} frames
              </span>
            )}
          </div>
          <div className="min-h-0 flex-1 relative">
            {agent.enable_vision_evaluation ? (
              <CameraCapture
                isRecording={isRecording && cameraOn}
                fps={agent.vision_analysis_fps || 2}
                onFrameCaptured={handleFrameCaptured}
                onError={handleCameraError}
                shouldInitialize={cameraOn}
                onVideoElementReady={handleCameraVideoElementReady}
              />
            ) : (
              <>
                {/* User webcam feed (always show when recording, even without vision analysis) */}
                <video
                  ref={userVideoRef}
                  autoPlay
                  playsInline
                  muted
                  className={`w-full h-full object-cover bg-muted ${webcamEnabled ? '' : 'hidden'}`}
                />
                {!webcamEnabled && (
                  <div className="w-full h-full bg-muted flex items-center justify-center">
                    <p className="text-muted-foreground text-sm">
                      {isRecording
                        ? (webcamFailureMessage || 'Webcam not available')
                        : 'Camera will activate when session starts'}
                    </p>
                  </div>
                )}
              </>
            )}
          </div>
        </div>

        {/* Avatar video fills the panel; do not remount when layout changes */}
        <div className={`session-video-tile min-w-0 flex flex-col bg-card border border-border rounded-xl overflow-hidden`}>
          <div className="shrink-0 min-h-11 bg-muted/40 px-3 py-2 border-b border-border flex items-center justify-between gap-2">
            <h3 className="min-w-0 truncate text-sm font-medium flex items-center gap-2">
              <User className="w-4 h-4 text-primary" />
              {agent.name}
            </h3>
            {anam.isStreaming && (
              <span className="flex items-center gap-2 text-xs text-green-600 font-medium">
                <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse" />
                Connected
              </span>
            )}
          </div>
          <div className="min-h-0 flex-1 bg-muted relative">
            {!anam.isStreaming && !anam.error && (
              <div className="absolute inset-0 flex items-center justify-center">
                <div className="text-center">
                  <Video className="w-16 h-16 text-muted-foreground mx-auto mb-3 opacity-50" />
                  <p className="text-sm text-muted-foreground">Avatar will appear when you start</p>
                </div>
              </div>
            )}
            <video
              id="anam-avatar-video"
              ref={videoContainerRef}
              autoPlay
              playsInline
              className="session-avatar-video absolute inset-0 w-full h-full"
              style={{ display: anam.isStreaming ? 'block' : 'none' }}
            >
              Your browser does not support video.
            </video>
          </div>
        </div>
        {agent.type === 'patient' && <div className={cameraOn ? 'md:col-span-2' : 'min-w-0'}><PatientSessionInformation agent={agent} /></div>}
      </div>

      {/* Professional Controls Section */}
        <div className={`session-actions ${isRecording ? 'sticky bottom-0 z-10' : ''} flex flex-col items-center gap-2 rounded-xl border bg-background/95 px-3 py-2 shadow-sm`}>
        {!isRecording ? (
          <div className="text-center">
            <div className="mb-4 mx-auto max-w-md rounded-lg border bg-card px-4 py-3 text-left shadow-sm">
              <div className="flex items-start justify-between gap-4">
                <div>
                  <p className="text-sm font-medium">Save session recording</p>
                  <p className="mt-1 text-xs text-muted-foreground">
                    Turn this off if you prefer not to store audio/video replay. Evaluation still works from the live session context.
                  </p>
                </div>
                <Switch
                  checked={recordSession}
                  onCheckedChange={setRecordSession}
                  aria-label="Save session recording"
                />
              </div>
            </div>
            <Button
              onClick={handleStartSimulation}
              disabled={!anam.isConnected || !!anam.error || isAnalyzing}
              size="lg"
              className="gap-2 px-6 py-3 text-base font-semibold"
            >
              <Phone className="w-6 h-6" />
              Start Simulation
            </Button>
            <p className="text-sm text-muted-foreground mt-3 max-w-md">
              Click to begin your conversation with {agent.name}
            </p>
            {anam.error && (
              <div className="mt-3 text-sm text-destructive bg-destructive/10 px-4 py-2 rounded-lg border border-destructive/20 inline-block">
                Avatar connection failed. Please try again or contact support.
              </div>
            )}
          </div>
        ) : (
          <div className="text-center">
            <Button
              onClick={handleEndSimulation}
              variant="destructive"
              size="lg"
              className="gap-2 px-6 py-3 text-base font-semibold"
            >
              <PhoneOff className="w-6 h-6" />
              End Session
            </Button>
            <p className="sr-only">
              Click when you're ready to finish and see your evaluation
            </p>
          </div>
        )}
      </div>

      <ResultGenerationDialog
        open={isAnalyzing}
        title="Generating your simulation results"
        status={analysisStatus || 'Saving your session and preparing your evaluation.'}
      />
    </div>
    </SessionPresentation>
  );
}
